import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandHoldingDollar,
  faHandshake,
  faUsersViewfinder,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  PartnerPaywallVariant,
  normalizePartnerIdentifier,
} from "partners/partnerExperiences";
import { getPartnerParameters } from "services/SauceService";
import "./Paywall.scss";

type Props = {
  variant: PartnerPaywallVariant;
  partner?: string | null;
  loading: boolean;
  onCtaClick: () => void;
  ctaLabel: string;
  onClose?: () => void;
  showCloseButton?: boolean;
  desktopTrustedByText?: string;
  mobileTrustedByText?: string;
  showTerms?: boolean;
};

const toPartnerDisplayName = (partnerKey: string): string => {
  const normalized = partnerKey.trim().toLowerCase();
  if (!normalized) {
    return "Partner";
  }

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
};

const getTrialLabel = (trialDays?: number | null): string => {
  if (!trialDays || trialDays <= 0) {
    return "month";
  }

  if (trialDays % 30 === 0) {
    const months = trialDays / 30;
    return months === 1 ? "month" : `${months} months`;
  }

  return trialDays === 1 ? "day" : `${trialDays} days`;
};

const PartnerPaywallContent: React.FC<Props> = ({
  variant,
  partner,
  loading,
  onCtaClick,
  ctaLabel,
  onClose,
  showCloseButton = true,
  desktopTrustedByText,
  mobileTrustedByText,
  showTerms = true,
}) => {
  const normalizedPartner = useMemo(
    () => normalizePartnerIdentifier(partner),
    [partner],
  );
  const [partnerDisplayName, setPartnerDisplayName] = useState<string | null>(
    null,
  );
  const [trialDays, setTrialDays] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPartnerParameters = async () => {
      if (!normalizedPartner) {
        setPartnerDisplayName(null);
        setTrialDays(null);
        return;
      }

      const fallbackName = toPartnerDisplayName(normalizedPartner);

      try {
        const response = await getPartnerParameters(normalizedPartner);
        if (!isMounted) {
          return;
        }

        const nextDisplayName =
          typeof response?.displayName === "string"
            ? response.displayName.trim()
            : "";
        const nextTrialDays =
          typeof response?.trialDays === "number" && response.trialDays > 0
            ? response.trialDays
            : null;

        setPartnerDisplayName(nextDisplayName || fallbackName);
        setTrialDays(nextTrialDays);
      } catch (_error) {
        if (isMounted) {
          setPartnerDisplayName(fallbackName);
          setTrialDays(null);
        }
      }
    };

    void loadPartnerParameters();

    return () => {
      isMounted = false;
    };
  }, [normalizedPartner]);

  const resolvedPartnerName =
    partnerDisplayName ||
    (normalizedPartner ? toPartnerDisplayName(normalizedPartner) : "Partner");
  const trialLabel = getTrialLabel(trialDays);
  const badgeLabel = normalizedPartner
    ? `Exclusive ${resolvedPartnerName} Offer`
    : "Exclusive Partner Offer";
  const titleLabel = normalizedPartner
    ? `${resolvedPartnerName} x Mustard Partnership`
    : "Mustard Partnership Program";
  const effectiveDesktopTrustedByText =
    desktopTrustedByText || `Card required - No charge for first ${trialLabel}`;
  const effectiveMobileTrustedByText =
    mobileTrustedByText || `Card required - No charge for first ${trialLabel}`;

  return (
    <div className={`pw-partner pw-partner--${variant}`}>
      <section className="pw-left pw-partner__left" aria-label="Plan details">
        <div className="pw-decor pw-decor--orange" />
        <div className="pw-decor pw-decor--blue" />
        <div className="pw-left-inner pw-partner__left-inner">
          <div className="pw-badge pw-partner__badge">
            <FontAwesomeIcon icon={faHandshake} aria-hidden="true" />
            <span>{badgeLabel}</span>
          </div>

          <h2 className="pw-title pw-partner__plan-title">{titleLabel}</h2>
          <p className="pw-sub pw-partner__plan-subtitle">
            Launch your first influencer campaign - free for your first{" "}
            {trialLabel}
          </p>

          <div className="pw-partner__price-wrap">
            <div className="pw-partner__price-row">
              <span className="pw-partner__price-main">FREE</span>
            </div>
            <p className="pw-partner__price-desc">for your first {trialLabel}</p>
            <p className="pw-partner__price-followup">
              Then $299/month after your first campaign
            </p>
          </div>

          <div className="pw-partner__desktop-cta">
            <button
              className={`pw-cta pw-partner__cta ${loading ? "pw-cta--loading" : ""}`}
              type="button"
              onClick={onCtaClick}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? "Processing..." : ctaLabel}
            </button>
            <p className="pw-small pw-partner__small-print">
              {effectiveDesktopTrustedByText}
            </p>
            {showTerms ? (
              <p className="pw-terms pw-partner__terms">
                By subscribing, you agree to{" "}
                <a href="https://www.mustard.love/terms-of-service">
                  Mustard&apos;s Terms of Service
                </a>
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <section
        className="pw-right pw-partner__right"
        aria-label="Offer details"
      >
        {showCloseButton && onClose ? (
          <button
            className="pw-close pw-partner__close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        ) : null}

        <div className="pw-right-scroll pw-partner__right-scroll">
          <section className="pw-partner__section">
            <h4 className="pw-list-title">What happens next:</h4>
            <div className="pw-partner__steps">
              <article>
                <div className="pw-partner__step-index">1</div>
                <h5>Start reaching local creators</h5>
                <p>Launch instantly with our AI tool.</p>
              </article>
              <article>
                <div className="pw-partner__step-index">2</div>
                <h5>AI + curated recommendations</h5>
                <p>Get matched with perfect creators.</p>
              </article>
              <article>
                <div className="pw-partner__step-index">3</div>
                <h5>Start booking creators</h5>
                <p>Receive applications within days.</p>
              </article>
            </div>
          </section>

          <section className="pw-partner__section pw-partner__section--included">
            <div className="pw-partner__included-items">
              <h4 className="pw-list-title">What&apos;s included</h4>
              <div className="pw-item pw-partner__included-item">
                <span className="pw-icon pw-icon--orange">
                  <FontAwesomeIcon icon={faUsersViewfinder} />
                </span>
                <div className="pw-item-body">
                  <h5>Guaranteed Applications</h5>
                  <p>
                    Invite creators and get guaranteed 3+ applications each
                    month.
                  </p>
                </div>
              </div>
              <div className="pw-item pw-partner__included-item">
                <span className="pw-icon pw-icon--blue">
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </span>
                <div className="pw-item-body">
                  <h5>AI + Manual Matching</h5>
                  <p>
                    Use AI to match creators or handpick from applications —
                    your choice.
                  </p>
                </div>
              </div>
              <div className="pw-item pw-partner__included-item">
                <span className="pw-icon pw-icon--green">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </span>
                <div className="pw-item-body">
                  <h5>Flexible Compensation</h5>
                  <p>
                    Provide flexible compensation options, including food, cash,
                    or affiliate commissions.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>

      <div className="pw-partner__mobile-footer">
        <button
          className={`pw-cta pw-partner__cta ${loading ? "pw-cta--loading" : ""}`}
          type="button"
          onClick={onCtaClick}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Processing..." : ctaLabel}
        </button>
        <p className="pw-small pw-partner__small-print">
          {effectiveMobileTrustedByText}
        </p>
      </div>
    </div>
  );
};

export default PartnerPaywallContent;
